import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { usePageAccess } from "../../../hooks/useAccessControl";
import formatDate from "../../../utils/formatDate";
import { 
  Search, 
  Plus,
  DollarSign,
  TrendingUp,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Filter,
  Target,
  Users,
  Eye,
  Edit3,
  Save,
  X,
  Download,
  Upload,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useUser } from "../../../context/UserContext";
import CreateCommissionModal from "../../../components/agent/commissions/CreateCommissionModal";
import { decodeHtmlEntities } from "../../../utils/textUtils";
import "../../../styles/modern-design-system.css";
import toast from "../../../utils/toast";

// Helper function to generate destination-specific booking references
const generateBookingReference = (destination = null) => {
  const destinationPrefixes = {
    'disney_world': 'DW',
    'disneyland': 'DL', 
    'universal_orlando': 'UO',
    'disney_cruise': 'DC',
    'universal_hollywood': 'UH'
  };
  
  const prefix = destinationPrefixes[destination] || 'BK';
  const randomSuffix = Math.random().toString(36).substr(2, 8).toUpperCase();
  return `${prefix}-${randomSuffix}`;
};

export default function CRMCommissionTracking() {
  const [commissions, setCommissions] = useState([]);
  const [trips, setTrips] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [amountFilter, setAmountFilter] = useState("all");
  
  // Analytics
  const [analytics, setAnalytics] = useState({
    totalCommissions: 0,
    pendingCommissions: 0,
    paidCommissions: 0,
    cancelledCommissions: 0,
    totalEarned: 0,
    totalPending: 0,
    avgCommission: 0,
    monthlyEarnings: 0
  });

  // Editing state
  const [editingCommission, setEditingCommission] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);

  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const { hasAccess } = usePageAccess('COMMISSIONS');

  // Helper function to get client initials
  const getClientInitials = (fullName) => {
    if (!fullName) return "C";
    const names = fullName.trim().split(" ");
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return fullName.charAt(0).toUpperCase();
  };

  // Redirect if no access
  useEffect(() => {
    if (!hasAccess) {
      navigate("/agent/dashboard");
    }
  }, [hasAccess, navigate]);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Listen for trip status changes from other components
  useEffect(() => {
    const handleTripStatusChange = (event) => {
      const { tripId, newStatus } = event.detail;
      syncCommissionWithTripStatus(tripId, newStatus);
    };

    const handleTripCreated = (event) => {
      const { trip, client } = event.detail;
      addCommissionForTrip(trip, client);
    };

    const handleCommissionUpdated = (event) => {
      const { tripId, commissionData, action } = event.detail;
      
      if (action === 'updated') {
        // Update existing commission or create new one
        const existingCommission = commissions.find(c => c.tripId === tripId);
        
        if (existingCommission) {
          // Update existing commission
          const updatedCommissions = commissions.map(commission => 
            commission.tripId === tripId 
              ? {
                  ...commission,
                  commissionAmount: commissionData.amount || commission.commissionAmount,
                  commissionRate: commissionData.rate ? Math.round(commissionData.rate * 100) : commission.commissionRate,
                  tripValue: commissionData.tripValue || commission.tripValue,
                  status: commissionData.status || commission.status
                }
              : commission
          );
          setCommissions(updatedCommissions);
          calculateAnalytics(updatedCommissions);
        } else {
          // Create new commission if it doesn't exist
          const newCommission = {
            id: `comm_${tripId}_${Date.now()}`,
            tripId: tripId,
            clientId: commissionData.clientId || '',
            clientName: commissionData.clientName || 'Unknown Client',
            tripName: commissionData.tripName || 'Trip',
            tripValue: commissionData.tripValue || 0,
            commissionRate: commissionData.rate ? Math.round(commissionData.rate * 100) : 0,
            commissionAmount: commissionData.amount || 0,
            status: commissionData.status || 'pending',
            expectedDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            paidDate: null,
            createdAt: new Date().toISOString(),
            notes: `Commission for trip`,
            bookingReference: generateBookingReference(commissionData.destination),
            supplier: 'Theme Park Resort',
            tripStatus: 'confirmed'
          };
          
          setCommissions(prev => [...prev, newCommission]);
          calculateAnalytics([...commissions, newCommission]);
        }
      }
    };

    // Listen for custom events from trip management
    window.addEventListener('tripStatusChanged', handleTripStatusChange);
    window.addEventListener('tripCreated', handleTripCreated);
    window.addEventListener('commissionUpdated', handleCommissionUpdated);

    return () => {
      window.removeEventListener('tripStatusChanged', handleTripStatusChange);
      window.removeEventListener('tripCreated', handleTripCreated);
      window.removeEventListener('commissionUpdated', handleCommissionUpdated);
    };
  }, [commissions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch real commission data from the backend
        const commissionRes = await fetch(`${import.meta.env.VITE_API_URL}/api/commission/tracking`, { 
          credentials: "include" 
        });
        
        if (commissionRes.ok) {
          const commissionData = await commissionRes.json();
          const realCommissions = commissionData.data?.commissions || [];
          
          // Transform backend data to match frontend format
          const transformedCommissions = realCommissions.map(commission => ({
            id: commission.commissionid,
            tripId: commission.tripid,
            clientId: commission.clientid || '',
            clientName: commission.client_name || 'Unknown Client',
            tripName: commission.trip_name || 'Trip',
            tripValue: commission.trip_value || 0,
            commissionRate: Math.round((commission.rate || 0) * 100), // Convert decimal to percentage
            commissionAmount: commission.amount || 0,
            status: commission.status || 'pending',
            expectedDate: commission.expected_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            paidDate: commission.payment_date || null,
            createdAt: commission.createdat || new Date().toISOString(),
            notes: commission.notes || `Commission for trip`,
            bookingReference: commission.booking_reference || generateBookingReference(commission.destination),
            supplier: commission.supplier || 'Theme Park Resort',
            tripStatus: commission.trip_status || 'confirmed'
          }));
          
          setCommissions(transformedCommissions);
          calculateAnalytics(transformedCommissions);
        } else {
          // Commission endpoint failed - show error and empty state
          console.error("Commission endpoint failed with status:", commissionRes.status);
          console.error("Commission endpoint response:", await commissionRes.text());
          toast.error("Failed to load commission data from server");
          setCommissions([]);
          calculateAnalytics([]);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        toast.error("Failed to load commission data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Mock data generation removed - using real API data only

  const getSupplierForDestination = (destination) => {
    const suppliers = {
      'disney_world': 'Disney Travel Company',
      'disneyland': 'Disneyland Resort',
      'universal_orlando': 'Universal Orlando Resort',
      'disney_cruise': 'Disney Cruise Line'
    };
    return suppliers[destination] || 'Theme Park Resort';
  };

  const calculateAnalytics = (commissionsData) => {
    const total = commissionsData.length;
    const pending = commissionsData.filter(c => c.status === 'pending').length;
    const paid = commissionsData.filter(c => c.status === 'paid').length;
    const cancelled = commissionsData.filter(c => c.status === 'cancelled').length;
    
    // Safely calculate totals with proper number handling
    const totalEarned = commissionsData
      .filter(c => c.status === 'paid')
      .reduce((sum, c) => {
        const amount = parseFloat(c.commissionAmount) || 0;
        return sum + amount;
      }, 0);
    
    const totalPending = commissionsData
      .filter(c => c.status === 'pending')
      .reduce((sum, c) => {
        const amount = parseFloat(c.commissionAmount) || 0;
        return sum + amount;
      }, 0);
    
    const avgCommission = paid > 0 ? Math.round(totalEarned / paid) : 0;
    
    // Monthly earnings (last 30 days)
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    const monthlyEarnings = commissionsData
      .filter(c => c.status === 'paid' && new Date(c.paidDate) >= thirtyDaysAgo)
      .reduce((sum, c) => {
        const amount = parseFloat(c.commissionAmount) || 0;
        return sum + amount;
      }, 0);

    setAnalytics({
      totalCommissions: total,
      pendingCommissions: pending,
      paidCommissions: paid,
      cancelledCommissions: cancelled,
      totalEarned: totalEarned,
      totalPending: totalPending,
      avgCommission: avgCommission,
      monthlyEarnings: monthlyEarnings
    });
  };

  const handleStatusUpdate = async (commissionId, newStatus) => {
    try {
      // Call the backend API to update commission status
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/commission/booking/${commissionId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          paymentDate: newStatus === 'paid' ? new Date().toISOString() : null
        })
      });

      if (response.ok) {
        const updatedCommissions = commissions.map(commission => 
          commission.id === commissionId 
            ? { 
                ...commission, 
                status: newStatus,
                paidDate: newStatus === 'paid' ? new Date().toISOString() : commission.paidDate
              }
            : commission
        );
        
        setCommissions(updatedCommissions);
        calculateAnalytics(updatedCommissions);
        toast.success(`Commission status updated to ${newStatus}`);
      } else {
        throw new Error('Failed to update commission status');
      }
    } catch (error) {
      console.error('Error updating commission status:', error);
      toast.error('Failed to update commission status');
    }
  };

  // Sync commission status when trip status changes
  const syncCommissionWithTripStatus = (tripId, newTripStatus) => {
    const updatedCommissions = commissions.map(commission => {
      if (commission.tripId === tripId) {
        let newCommissionStatus = commission.status;
        let paidDate = commission.paidDate;
        
        switch (newTripStatus) {
          case 'confirmed':
            newCommissionStatus = 'pending';
            break;
          case 'completed':
            // Keep existing status if already paid, otherwise set to pending
            if (commission.status !== 'paid') {
              newCommissionStatus = 'pending';
            }
            break;
          case 'cancelled':
            newCommissionStatus = 'cancelled';
            break;
          case 'pending':
            // Remove commission for pending trips
            return null;
        }
        
        return {
          ...commission,
          status: newCommissionStatus,
          tripStatus: newTripStatus,
          paidDate: newCommissionStatus === 'paid' ? paidDate : null
        };
      }
      return commission;
    }).filter(Boolean);
    
    setCommissions(updatedCommissions);
    calculateAnalytics(updatedCommissions);
  };

  // Add new commission when trip is confirmed
  const addCommissionForTrip = (trip, client) => {
    if (trip.status !== 'confirmed' && trip.status !== 'completed') {
      return;
    }
    
    const tripValue = Math.floor(Math.random() * 5000) + 1000;
    const commissionRate = Math.random() * 0.15 + 0.05;
    const commissionAmount = Math.round(tripValue * commissionRate);
    
    const newCommission = {
      id: `comm_${trip.tripid}_${Date.now()}`,
      tripId: trip.tripid,
      clientId: trip.clientid,
      clientName: client?.fullname || trip.fullname || "Unknown Client",
      tripName: decodeHtmlEntities(trip.tripname) || `${client?.fullname || 'Client'}'s Trip`,
      tripValue: tripValue,
      commissionRate: Math.round(commissionRate * 100),
      commissionAmount: commissionAmount,
      status: trip.status === 'completed' ? 'pending' : 'pending',
      expectedDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      paidDate: null,
      createdAt: new Date().toISOString(),
      notes: `Commission for ${trip.destination || 'Disney World'} trip`,
      bookingReference: generateBookingReference(trip.destination),
      supplier: getSupplierForDestination(trip.destination),
      tripStatus: trip.status
    };
    
    setCommissions(prev => [...prev, newCommission]);
    calculateAnalytics([...commissions, newCommission]);
  };

  const handleEditCommission = (commission) => {
    setEditingCommission(commission);
    setEditFormData({
      commissionAmount: commission.commissionAmount,
      commissionRate: commission.commissionRate,
      expectedDate: commission.expectedDate,
      notes: commission.notes || '',
      bookingReference: commission.bookingReference
    });
  };

  const handleSaveCommission = async () => {
    try {
      setSaving(true);
      
      // Call the backend API to update commission
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/commission/booking/${editingCommission.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: editFormData.commissionAmount,
          rate: editFormData.commissionRate / 100, // Convert percentage to decimal
          expectedDate: editFormData.expectedDate,
          notes: editFormData.notes,
          bookingReference: editFormData.bookingReference
        })
      });

      if (response.ok) {
        const updatedCommissions = commissions.map(commission => 
          commission.id === editingCommission.id 
            ? { 
                ...commission, 
                ...editFormData,
                commissionAmount: editFormData.commissionAmount,
                commissionRate: editFormData.commissionRate
              }
            : commission
        );
        
        setCommissions(updatedCommissions);
        calculateAnalytics(updatedCommissions);
        setEditingCommission(null);
        setEditFormData({});
        toast.success("Commission updated successfully!");
      } else {
        throw new Error('Failed to update commission');
      }
    } catch (error) {
      console.error('Error updating commission:', error);
      toast.error('Failed to update commission');
    } finally {
      setSaving(false);
    }
  };

  const handleCommissionCreated = (newCommission) => {
    // Add the new commission to the list
    const updatedCommissions = [...commissions, newCommission];
    setCommissions(updatedCommissions);
    calculateAnalytics(updatedCommissions);
    toast.success("Commission created successfully!");
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'pending': {
        label: 'Pending',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      'paid': {
        label: 'Paid',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      },
      'cancelled': {
        label: 'Cancelled',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: AlertCircle
      }
    };
    return statusMap[status] || statusMap['pending'];
  };

  const formatCurrency = (amount) => {
    // Handle NaN, undefined, or null values
    const safeAmount = isNaN(amount) || amount === null || amount === undefined ? 0 : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(safeAmount);
  };

  // formatDate imported from utils/formatDate

  const filteredCommissions = commissions.filter((commission) => {
    const matchesSearch = 
      commission.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      commission.tripName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      commission.bookingReference.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || commission.status === statusFilter;
    
    let matchesDate = true;
    if (dateFilter !== "all") {
      const now = new Date();
      const commissionDate = new Date(commission.expectedDate);
      const daysDiff = Math.ceil((commissionDate - now) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case "overdue":
          matchesDate = commission.status === 'pending' && daysDiff < 0;
          break;
        case "this_week":
          matchesDate = daysDiff >= 0 && daysDiff <= 7;
          break;
        case "this_month":
          matchesDate = daysDiff >= 0 && daysDiff <= 30;
          break;
      }
    }
    
    let matchesAmount = true;
    if (amountFilter !== "all") {
      switch (amountFilter) {
        case "high":
          matchesAmount = commission.commissionAmount >= 1000;
          break;
        case "medium":
          matchesAmount = commission.commissionAmount >= 500 && commission.commissionAmount < 1000;
          break;
        case "low":
          matchesAmount = commission.commissionAmount < 500;
          break;
      }
    }
    
    return matchesSearch && matchesStatus && matchesDate && matchesAmount;
  });

  if (!hasAccess) {
    return null; // Will redirect
  }

  return (
    <>
      <div className="max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="flex flex-col gap-4 justify-between mb-4 md:flex-row md:items-center animate-fade-in-up">
              <div>
                <h1 className="text-xl font-bold tracking-tight leading-tight text-black sm:text-2xl lg:text-3xl text-balance">
                  Commission Tracking
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  Track and manage commission earnings from trip bookings
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {/* Export functionality */}}
                  className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Commission
                </button>
              </div>
            </div>

            {/* Enhanced Analytics Cards - matching CRMTripManagement styling */}
            <div className="grid grid-cols-2 gap-3 mb-4 sm:grid-cols-4 lg:grid-cols-8 animate-fade-in-up sm:gap-4">
              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Total</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.totalCommissions}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Pending</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.pendingCommissions}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Paid</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.paidCommissions}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Cancelled</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{analytics.cancelledCommissions}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Total Earned</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{formatCurrency(analytics.totalEarned)}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Pending Amount</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{formatCurrency(analytics.totalPending)}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">Avg Commission</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{formatCurrency(analytics.avgCommission)}</p>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-600 sm:text-sm">This Month</p>
                    <p className="text-xl font-bold text-black sm:text-2xl">{formatCurrency(analytics.monthlyEarnings)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between animate-fade-in-up">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search commissions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="py-2 pr-3 pl-10 w-full text-sm rounded-lg border border-gray-300 placeholder:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <button
                  className={`crm-filter-btn ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter size={18} />
                  <span>Filters</span>
                  {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
              
              <div className="text-xs text-gray-600 sm:text-sm">
                {filteredCommissions.length} of {commissions.length} commissions
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="p-4 mb-4 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Status</label>
                    <div className="relative">
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Date Range</label>
                    <div className="relative">
                      <select
                        value={dateFilter}
                        onChange={(e) => setDateFilter(e.target.value)}
                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                      >
                        <option value="all">All Dates</option>
                        <option value="overdue">Overdue</option>
                        <option value="this_week">This Week</option>
                        <option value="this_month">This Month</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">Amount</label>
                    <div className="relative">
                      <select
                        value={amountFilter}
                        onChange={(e) => setAmountFilter(e.target.value)}
                        className="px-3 py-2 pr-8 w-full text-sm text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400"
                        style={{ appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none' }}
                      >
                        <option value="all">All Amounts</option>
                        <option value="high">High ($1000+)</option>
                        <option value="medium">Medium ($500-$999)</option>
                        <option value="low">Low (Under $500)</option>
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Commissions Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-3 border-b border-gray-200 sm:p-4">
                <div>
                  <h2 className="text-base font-semibold text-black sm:text-lg">
                    Commission Records
                  </h2>
                  <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                    Track and manage all commission earnings
                  </p>
                </div>
              </div>

              <div className="p-3 sm:p-4">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="flex gap-2 items-center">
                      <div className="w-6 h-6 rounded-full border-2 border-black animate-spin border-t-transparent"></div>
                      <span className="text-sm text-gray-600">Loading commissions...</span>
                    </div>
                  </div>
                ) : filteredCommissions.length === 0 ? (
                  <div className="flex flex-col justify-center items-center px-6 py-12 w-full text-center">
                    <DollarSign className="mb-4 w-12 h-12 text-gray-400" />
                    <h2 className="mb-2 text-lg font-semibold text-gray-800 sm:text-xl">
                      No Commissions Found
                    </h2>
                    <p className="mb-6 max-w-md text-sm text-gray-600">
                      {searchQuery || statusFilter !== "all" || dateFilter !== "all" || amountFilter !== "all"
                        ? "Try adjusting your search or filters."
                        : "No commission records found. Commissions are generated when trips are booked."
                      }
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Client</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Trip</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Trip Status</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Supplier</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Trip Value</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Rate</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Commission</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Status</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Expected Date</th>
                          <th className="px-2 py-3 font-medium text-left text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCommissions.map((commission) => {
                          const statusInfo = getStatusInfo(commission.status);
                          const StatusIcon = statusInfo.icon;
                          
                          return (
                            <tr key={commission.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-2 py-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-700 rounded-lg flex items-center justify-center">
                                    <span className="text-xs font-bold text-white">
                                      {getClientInitials(commission.clientName)}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900">{commission.clientName}</div>
                                    <div className="text-xs text-gray-500">{commission.bookingReference}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-3">
                                <div className="font-medium text-gray-900">{commission.tripName}</div>
                              </td>
                              <td className="px-2 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${
                                  commission.tripStatus === 'confirmed' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                                  commission.tripStatus === 'completed' ? 'bg-green-100 text-green-800 border-green-200' :
                                  commission.tripStatus === 'cancelled' ? 'bg-red-100 text-red-800 border-red-200' :
                                  'bg-gray-100 text-gray-800 border-gray-200'
                                }`}>
                                  {commission.tripStatus === 'confirmed' ? <CheckCircle className="w-3 h-3" /> :
                                   commission.tripStatus === 'completed' ? <CheckCircle className="w-3 h-3" /> :
                                   commission.tripStatus === 'cancelled' ? <AlertCircle className="w-3 h-3" /> :
                                   <Clock className="w-3 h-3" />}
                                  {commission.tripStatus?.charAt(0).toUpperCase() + commission.tripStatus?.slice(1) || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-2 py-3 text-gray-600">{commission.supplier}</td>
                              <td className="px-2 py-3 font-medium text-gray-900">{formatCurrency(commission.tripValue)}</td>
                              <td className="px-2 py-3 text-gray-600">{commission.commissionRate}%</td>
                              <td className="px-2 py-3 font-bold text-gray-900">{formatCurrency(commission.commissionAmount)}</td>
                              <td className="px-2 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${statusInfo.color}`}>
                                  <StatusIcon className="w-3 h-3" />
                                  {statusInfo.label}
                                </span>
                              </td>
                              <td className="px-2 py-3 text-gray-600">{formatDate(commission.expectedDate, { format: 'medium' })}</td>
                              <td className="px-2 py-3">
                                <div className="flex gap-1 items-center">
                                  <div className="relative">
                                    <select
                                      value={commission.status}
                                      onChange={(e) => handleStatusUpdate(commission.id, e.target.value)}
                                      className="appearance-none px-3 py-1.5 pr-8 text-xs font-medium rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 hover:border-gray-400 cursor-pointer"
                                    >
                                      <option value="pending">Pending</option>
                                      <option value="paid">Paid</option>
                                      <option value="cancelled">Cancelled</option>
                                    </select>
                                    <div className="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none">
                                      <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleEditCommission(commission)}
                                    className="p-1 text-gray-400 rounded-md transition-colors hover:text-blue-600 hover:bg-blue-50"
                                    title="Edit commission"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => {/* View details */}}
                                    className="p-1 text-gray-400 rounded-md transition-colors hover:text-gray-600 hover:bg-gray-50"
                                    title="View details"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

      {/* Create Commission Modal */}
      <CreateCommissionModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCommissionCreated={handleCommissionCreated}
      />

      {/* Edit Commission Modal */}
      {editingCommission && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="p-6 w-full max-w-md bg-white rounded-lg shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Commission</h3>
              <button
                onClick={() => {
                  setEditingCommission(null);
                  setEditFormData({});
                }}
                className="p-1 text-gray-400 rounded-md transition-colors hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Commission Amount</label>
                <input
                  type="number"
                  value={editFormData.commissionAmount}
                  onChange={(e) => setEditFormData({...editFormData, commissionAmount: parseFloat(e.target.value)})}
                  className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Commission Rate (%)</label>
                <input
                  type="number"
                  value={editFormData.commissionRate}
                  onChange={(e) => setEditFormData({...editFormData, commissionRate: parseFloat(e.target.value)})}
                  className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Expected Date</label>
                <input
                  type="date"
                  value={editFormData.expectedDate ? editFormData.expectedDate.split('T')[0] : ''}
                  onChange={(e) => setEditFormData({...editFormData, expectedDate: e.target.value})}
                  className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Booking Reference</label>
                <input
                  type="text"
                  value={editFormData.bookingReference}
                  onChange={(e) => setEditFormData({...editFormData, bookingReference: e.target.value})}
                  className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block mb-1 text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  rows={3}
                  className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end mt-6">
              <button
                onClick={() => {
                  setEditingCommission(null);
                  setEditFormData({});
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md transition-colors hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCommission}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
